import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { jwtDecode } from "jwt-decode";
import apiClient from "../../apiClient";
import "../../styles/navigation/DepositoryFiles.css";

const FLMUpload = () => {
  const [currentStep, setCurrentStep] = useState(1);
  const [selectedFile, setSelectedFile] = useState(null);
  const [selectedReportType, setSelectedReportType] = useState(null);
  const [processing, setProcessing] = useState(false);
  const [uploadHistory, setUploadHistory] = useState([]);
  const [message, setMessage] = useState({ type: "", text: "" });
  const [isDragging, setIsDragging] = useState(false);
  const [uploadResult, setUploadResult] = useState(null);

  // ✅ NEW: Status tracking state
  const [processingStage, setProcessingStage] = useState("");
  const [processingProgress, setProcessingProgress] = useState(0);
  const [processingMessage, setProcessingMessage] = useState("");
  const [uploadId, setUploadId] = useState(null); // eslint-disable-line no-unused-vars

  const fileInputRef = useRef(null);
  const navigate = useNavigate();
  const pollingIntervalRef = useRef(null); // ✅ NEW: Ref for polling interval

  useEffect(() => {
    const token = localStorage.getItem("authToken");
    if (!token) {
      navigate("/");
      return;
    }
    try {
      const { exp } = jwtDecode(token);
      if (exp < Date.now() / 1000) {
        localStorage.removeItem("authToken");
        navigate("/");
      }
    } catch (err) {
      console.error("Invalid token:", err);
      localStorage.removeItem("authToken");
      navigate("/");
    }
  }, [navigate]);

  useEffect(() => {
    if (currentStep === 1) fetchUploadHistory();
  }, [currentStep]);

  // ✅ NEW: Cleanup polling on unmount
  useEffect(() => {
    return () => {
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
      }
    };
  }, []);

  const fetchUploadHistory = async () => {
    try {
      const response = await apiClient.get(`/api/flm/upload-history?ts=${Date.now()}`);
      setUploadHistory(response.data);
    } catch (err) {
      console.error("History fetch failed:", err);
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    setIsDragging(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragging(false);
    if (e.dataTransfer.files[0]) validateAndSetFile(e.dataTransfer.files[0]);
  };

  const handleFileSelect = (e) =>
    e.target.files[0] && validateAndSetFile(e.target.files[0]);

  const isExcel = (file) =>
    [
      "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      "application/vnd.ms-excel",
    ].includes(file.type);

  const validateAndSetFile = (file) => {
    if (isExcel(file)) {
      setSelectedFile(file);
      setMessage({ type: "", text: "" });
    } else {
      setMessage({
        type: "error",
        text: "Please select a valid Excel file (.xlsx or .xls)",
      });
    }
  };

  const handleProceedToSelection = () => {
    if (selectedFile) {
      setCurrentStep(2);
      setMessage({ type: "", text: "" });
    }
  };

  const handleReportTypeSelection = (type) => {
    setSelectedReportType(type);
  };

  // ✅ NEW: Poll upload status
  const pollUploadStatus = async (uploadId) => {
    try {
      const response = await apiClient.get(`/api/flm/upload-status/${uploadId}`);
      const statusData = response.data;

      console.log("📊 Status Update:", statusData);

      // Update state with current status
      setProcessingStage(statusData.processing_stage || "processing");
      setProcessingProgress(statusData.processing_progress || 0);
      setProcessingMessage(statusData.processing_message || "Processing...");

      // Check if completed or failed
      if (statusData.status === "completed") {
        // Stop polling
        if (pollingIntervalRef.current) {
          clearInterval(pollingIntervalRef.current);
          pollingIntervalRef.current = null;
        }

        setProcessing(false);
        setMessage({
          type: "success",
          text: "Report generated successfully!",
        });

        // Set upload result
        setUploadResult({
          totalRecords: statusData.total_records,
          emailResults: {
            successful: statusData.emails_sent || 0,
            totalEmails: statusData.emails_total || 0,
          },
        });
      } else if (statusData.status === "failed") {
        // Stop polling
        if (pollingIntervalRef.current) {
          clearInterval(pollingIntervalRef.current);
          pollingIntervalRef.current = null;
        }

        setProcessing(false);
        setMessage({
          type: "error",
          text: statusData.error_message || "Processing failed",
        });
      }
      // If still processing, polling will continue automatically
    } catch (error) {
      console.error("❌ Status polling error:", error);
    }
  };

  // ✅ MODIFIED: Handle file processing with polling
  const handleProcessFile = async () => {
    if (!selectedFile || !selectedReportType) return;

    setProcessing(true);
    setCurrentStep(3);
    setProcessingStage("uploading");
    setProcessingProgress(5);
    setProcessingMessage("Uploading file...");

    const formData = new FormData();
    formData.append("file", selectedFile);
    formData.append("reportType", selectedReportType);

    try {
      console.log("🚀 Sending request...");
      console.log("   File:", selectedFile.name);
      console.log("   Report Type:", selectedReportType);

      const response = await apiClient.post(`/api/flm/upload?ts=${Date.now()}`, formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });

      console.log("📡 Response received");

      const data = response.data;

      if (data.uploadId) {
        // ✅ NEW: Store uploadId and start polling
        setUploadId(data.uploadId);
        setProcessingStage("processing");
        setProcessingProgress(10);
        setProcessingMessage("Processing started...");

        console.log(`✅ Upload started with ID: ${data.uploadId}`);
        console.log("🔄 Starting status polling...");

        // Start polling every 3 seconds
        pollingIntervalRef.current = setInterval(() => {
          pollUploadStatus(data.uploadId);
        }, 3000);

        // Do initial poll immediately
        pollUploadStatus(data.uploadId);
      } else {
        setMessage({
          type: "error",
          text: data.error || "Upload failed",
        });
        setProcessing(false);
      }
    } catch (error) {
      console.error("❌ Request failed:", error);
      const errorMessage = error.response?.data?.error || "Network error occurred";
      setMessage({ type: "error", text: errorMessage });
      setProcessing(false);
    }
  };

  const handleStartOver = () => {
    // ✅ NEW: Clear polling interval
    if (pollingIntervalRef.current) {
      clearInterval(pollingIntervalRef.current);
      pollingIntervalRef.current = null;
    }

    setCurrentStep(1);
    setSelectedFile(null);
    setSelectedReportType(null);
    setUploadResult(null);
    setMessage({ type: "", text: "" });
    setUploadId(null);
    setProcessingStage("");
    setProcessingProgress(0);
    setProcessingMessage("");
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const formatDate = (d) => new Date(d).toLocaleString();

  const reportTypes = [
    {
      id: "ctg-window-readiness",
      title: "CTG - Window Readiness",
      description: "Analyze training readiness for upcoming window",
      available: true,
    },
    {
      id: "ctg-learning-path",
      title: "CTG - Learning Path",
      description: "Track learning path completion progress",
      available: true,
    },
    {
      id: "workpulse-comparison",
      title: "WorkPulse vs CTG Comparison",
      description: "Compare WorkPulse and CTG data to identify variances",
      available: true,
    },
  ];

  const renderProgressBar = () => (
    <div
      className="progress-bar-container"
      style={{
        background: "#f5f7fa",
        padding: "50px 0",
        margin: "0 0 40px 0",
      }}
    >
      <div className="progress-bar">
        {["Upload File", "Select Report", "View Results"].map((label, i) => (
          <React.Fragment key={label}>
            <div
              className={`progress-step ${
                currentStep >= i + 1 ? "active" : ""
              } ${currentStep > i + 1 ? "completed" : ""}`}
            >
              <div className="step-number">{i + 1}</div>
              <div className="step-label">{label}</div>
            </div>
            {i < 2 && (
              <div
                className={`progress-line ${
                  currentStep > i + 1 ? "active" : ""
                }`}
              />
            )}
          </React.Fragment>
        ))}
      </div>
    </div>
  );

  const renderFileUploadStep = () => (
    <div className="upload-card">
      <h1 className="card-title">Upload Training Data</h1>

      <div className="instructions-section">
        <p className="instructions">
          Please upload your FLM training data. Supported formats include CTG
          Window Readiness and Learning Path reports. The system will process
          your data and generate comprehensive reports with email notifications.
        </p>
        <a
          href="/templates/FLM_Template.xlsx"
          className="template-link"
          download
        >
          Download Excel Template
        </a>
      </div>

      <div
        className={`drop-zone ${isDragging ? "dragging" : ""} ${
          selectedFile ? "has-file" : ""
        }`}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        onClick={() => fileInputRef.current?.click()}
      >
        <input
          ref={fileInputRef}
          type="file"
          accept=".xlsx,.xls"
          onChange={handleFileSelect}
          style={{ display: "none" }}
        />

        {selectedFile ? (
          <div className="file-info">
            <span className="file-icon">📄</span>
            <span className="file-name">{selectedFile.name}</span>
            <span className="file-size">
              ({(selectedFile.size / 1024).toFixed(2)} KB)
            </span>
            <button
              className="remove-file-btn"
              onClick={(e) => {
                e.stopPropagation();
                setSelectedFile(null);
                if (fileInputRef.current) fileInputRef.current.value = "";
              }}
            >
              Remove
            </button>
          </div>
        ) : (
          <div className="drop-zone-content">
            <span className="upload-icon">📤</span>
            <p>Drag and drop files here or click to upload</p>
          </div>
        )}
      </div>

      {selectedFile && (
        <button onClick={handleProceedToSelection} className="upload-button">
          Next: Select Report Type
        </button>
      )}

      {message.text && currentStep === 1 && (
        <div className={`message ${message.type}`}>{message.text}</div>
      )}

      {uploadHistory.length > 0 && (
        <div className="history-card">
          <h2 className="section-title">Recent Uploads</h2>
          <div className="history-table-container">
            <table className="history-table">
              <thead>
                <tr>
                  <th>File Name</th>
                  <th>Uploaded By</th>
                  <th>Upload Date</th>
                  <th>Records</th>
                  <th>Status</th>
                  <th>Report Type</th>
                </tr>
              </thead>
              <tbody>
                {uploadHistory.slice(0, 5).map((up) => (
                  <tr key={up.id}>
                    <td>
                      {up.file_path ? (
                        <a
                          href={`https://cafuamanagementcompany.sharepoint.com${up.file_path}`}
                          className="file-link"
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          {up.file_name}
                        </a>
                      ) : (
                        <span>{up.file_name}</span>
                      )}
                    </td>
                    <td>{up.uploaded_by}</td>
                    <td>{formatDate(up.uploaded_at)}</td>
                    <td>{up.total_records || "-"}</td>
                    <td className={`status ${up.status}`}>{up.status}</td>
                    <td>
                      {up.report_type === "ctg-window-readiness" &&
                        "Window Readiness"}
                      {up.report_type === "ctg-learning-path" &&
                        "Learning Path"}
                      {up.report_type === "workpulse-comparison" &&
                        "WorkPulse Comparison"}
                      {!up.report_type && "Legacy Report"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );

  const renderReportSelectionStep = () => (
    <div className="report-selection-card">
      <h1 className="card-title">Select Report Type</h1>
      <p className="selection-subtitle">
        Choose the type of report you want to generate from your training data
      </p>

      <div className="report-types-grid">
        {reportTypes.map((r) => (
          <div
            key={r.id}
            className={`report-type-card ${
              selectedReportType === r.id ? "selected" : ""
            } ${!r.available ? "disabled" : ""}`}
            onClick={() => r.available && handleReportTypeSelection(r.id)}
            style={{
              cursor: r.available ? "pointer" : "not-allowed",
              opacity: r.available ? 1 : 0.6,
              position: "relative",
            }}
          >
            {r.badge && <div className="report-badge">{r.badge}</div>}

            <div className="report-icon">
              {r.id === "ctg-window-readiness" && "📊"}
              {r.id === "ctg-learning-path" && "🎯"}
              {r.id === "workpulse-comparison" && "🔄"}
            </div>
            <h3 className="report-title">{r.title}</h3>
            <p className="report-description">{r.description}</p>
          </div>
        ))}
      </div>

      <div className="action-buttons">
        <button onClick={() => setCurrentStep(1)} className="secondary-button">
          Back
        </button>
        <button
          onClick={handleProcessFile}
          disabled={!selectedReportType}
          className="primary-button"
        >
          Generate Report
        </button>
      </div>

      {message.text && currentStep === 2 && (
        <div className={`message ${message.type}`}>{message.text}</div>
      )}
    </div>
  );

  // ✅ MODIFIED: Results step with live progress
  const renderResultsStep = () => (
    <div className="results-card">
      <h1 className="card-title">Report Results</h1>

      {processing ? (
        <div className="processing-container">
          <div className="processing-spinner" />

          {/* ✅ NEW: Progress bar */}
          <div className="progress-bar-wrapper">
            <div className="progress-bar-track">
              <div
                className="progress-bar-fill"
                style={{ width: `${processingProgress}%` }}
              />
            </div>
            <div className="progress-percentage">{processingProgress}%</div>
          </div>

          {/* ✅ NEW: Stage indicator */}
          <div className="processing-stage">
            {processingStage === "validating" && "📝 Validating data..."}
            {processingStage === "processing" && "💾 Processing records..."}
            {processingStage === "emailing" && "📧 Sending emails..."}
            {processingStage === "uploading" && "☁️ Uploading to SharePoint..."}
            {processingStage === "complete" && "✅ Complete!"}
          </div>

          {/* ✅ NEW: Detailed message */}
          <p className="processing-text">
            {processingMessage || "Processing your report..."}
            <br />
            <span className="processing-subtext">
              This may take several minutes for large files
            </span>
          </p>
        </div>
      ) : (
        <>
          {message.text && (
            <div className={`message ${message.type}`}>{message.text}</div>
          )}

          {uploadResult && message.type === "success" && (
            <div className="result-container">
              <div className="result-summary">
                <h3>Report Generated Successfully</h3>
                <div className="result-stats">
                  <div className="stat-item">
                    <span className="stat-label">Report Type:</span>
                    <span className="stat-value">
                      {selectedReportType === "ctg-window-readiness" &&
                        "Window Readiness"}
                      {selectedReportType === "ctg-learning-path" &&
                        "Learning Path"}
                      {selectedReportType === "workpulse-comparison" &&
                        "WorkPulse Comparison"}
                    </span>
                  </div>
                  <div className="stat-item">
                    <span className="stat-label">Total Records:</span>
                    <span className="stat-value">
                      {uploadResult.totalRecords || 0}
                    </span>
                  </div>
                  {uploadResult.totalLocations && (
                    <div className="stat-item">
                      <span className="stat-label">Total Locations:</span>
                      <span className="stat-value">
                        {uploadResult.totalLocations}
                      </span>
                    </div>
                  )}
                  {uploadResult.emailResults && (
                    <div className="stat-item">
                      <span className="stat-label">Emails Sent:</span>
                      <span className="stat-value">
                        {uploadResult.emailResults.successful} /{" "}
                        {uploadResult.emailResults.totalEmails}
                      </span>
                    </div>
                  )}
                  {uploadResult.overallCompletion !== undefined && (
                    <div className="stat-item">
                      <span className="stat-label">Overall Completion:</span>
                      <span
                        className="stat-value"
                        style={{
                          color:
                            uploadResult.overallCompletion >= 80
                              ? "#28a745"
                              : uploadResult.overallCompletion >= 60
                              ? "#ffc107"
                              : "#dc3545",
                          fontWeight: "bold",
                        }}
                      >
                        {uploadResult.overallCompletion}%
                      </span>
                    </div>
                  )}
                </div>
              </div>

              <div className="result-actions">
                <button className="primary-button" onClick={handleStartOver}>
                  Upload New File
                </button>
              </div>
            </div>
          )}

          {message.type === "error" && (
            <div className="error-actions">
              <button
                className="secondary-button"
                onClick={() => setCurrentStep(2)}
              >
                Try Different Report
              </button>
              <button className="primary-button" onClick={handleStartOver}>
                Start Over
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );

  return (
    <div className="flm-upload-page">
      <div className="flm-upload-container" style={{ paddingTop: "70px" }}>
        {currentStep > 0 && renderProgressBar()}
        <div className={`workflow-step step-${currentStep}`}>
          {currentStep === 1 && renderFileUploadStep()}
          {currentStep === 2 && renderReportSelectionStep()}
          {currentStep === 3 && renderResultsStep()}
        </div>
      </div>
    </div>
  );
};

export default FLMUpload;
